import { withPublicFontUrl } from '@lib/utils/cdn'
import { objectKeys } from '@lib/utils/object'

import { Fonts } from '@constants/enums/fonts'

export type TFontFamily = 'Super Sans' | 'Super Sans Mono' | 'Super Serif'
type TFontFileExt = 'woff2' | 'otf'
export type TFontFormat = 'opentype' | 'woff2'
export type TFontStyle = 'normal' | 'italic'
export type TFontWeights = 100 | 200 | 300 | 400 | 500 | 580 | 700 | 800 | 900

export type TFontFormats = {
  [key in TFontFileExt]?: string
}

export type TFonts = Record<
  TFontFamily,
  {
    readonly [key in TFontWeights]?: {
      [key in TFontStyle]?: TFontFormats
    }
  }
>

/**
 * Get format string based on file extension.
 * @param {string} file - Font file name.
 * @returns {TFontFormat} Font format for `format()`.
 */
const getFormatStringFromFileExt = (file: string): TFontFormat => {
  const [_, ext] = file.split('.')
  switch (ext) {
    case 'otf': {
      return 'opentype'
    }
    case 'woff2': {
      return 'woff2'
    }
    default: {
      throw new Error('Unprocessable file extension')
    }
  }
}

/**
 * Get list of resources for `src:` of `@font-face`.
 * @param {TFontFormats} settings - Font file name.
 * @returns {string[]}
 */
const getResources = (settings: TFontFormats): string[] => {
  const formats = objectKeys(settings)
  const resources = formats?.map(format => {
    const formatDefinition = settings[format]
    if (!formatDefinition) {
      return ''
    }
    return `url('${withPublicFontUrl(formatDefinition)}') format('${getFormatStringFromFileExt(formatDefinition)}')`
  })
  return resources.filter(x => x != '')
}

type TFlatFontDefinitions = {
  family: TFontFamily
  weight: TFontWeights
  style: TFontStyle
  formats: TFontFormats
}

/**
 * Generate flat font definitions.
 * @param {TFontFamily[]} families - Font names to include.
 * @returns {TFlatFontDefinitions[]} Array of flat font definitions.
 */
export const flatFontDefinitions = (families: TFontFamily[]): TFlatFontDefinitions[] => {
  const definitions: TFlatFontDefinitions[] = []
  for (const family of families) {
    const familyDefinition = Fonts[family]
    if (!familyDefinition) continue
    for (const weight of objectKeys(familyDefinition)) {
      const styleDefinition = familyDefinition[weight]
      if (!styleDefinition) continue
      for (const style of objectKeys(styleDefinition)) {
        const formatDefinition = styleDefinition[style]
        if (!formatDefinition) continue
        // Formats are not looped over because they should be
        // handled by code that calls this function
        definitions.push({ family, weight, style, formats: formatDefinition })
      }
    }
  }
  return definitions
}

/**
 * Get `@font-face` definitions for all specified fonts.
 * @param {TFontFamily[]} families - Font file name.
 * @returns {string} String of `@face-face` CSS.
 */
export const getStyleFontFaces = (families: TFontFamily[]): string => {
  const css: string[] = []
  for (const { family, weight, style, formats } of flatFontDefinitions(families)) {
    const src = getResources(formats)
    if (src.length === 0) continue
    css.push(`@font-face {
  font-family: "${family}";
  font-style: ${style};
  font-weight: ${weight};
  font-display: swap;
  src: ${src.join(',\n')};
}`)
  }

  return css.join('\n\n')
}

/**
 * Generate flattened font definitions for head <links>.
 * @param {TFontFamily[]} families - Font names to include.
 * @returns {Array<{key: string, url: string, format: string}>} Array of font preload link definitions.
 */
export const getFontsForPreloadLinks = (
  families: TFontFamily[],
): Array<{ key: string; url: string; format: string }> => {
  const links: Array<{ key: string; url: string; format: string }> = []
  for (const { family, weight, formats } of flatFontDefinitions(families)) {
    for (const format of objectKeys(formats)) {
      const file = formats[format]
      if (!file) continue
      links.push({
        key: `${family}-${weight}-${format}`,
        url: withPublicFontUrl(file),
        format,
      })
    }
  }
  return links
}
